import { baseKeymap, chainCommands } from "prosemirror-commands";
import { redo, undo } from "prosemirror-history";
import { keymap } from "prosemirror-keymap";
import { findNext, findPrev } from "prosemirror-search";
import {
  liftListItem,
  sinkListItem,
  splitListItem,
} from "prosemirror-schema-list";
import { Selection, type Command, type Plugin } from "prosemirror-state";
import { zwdocSchema } from "../../lib/zwdoc/schema";
import type { FormatCommand } from "../port";
import {
  insertLineBreak,
  insertTab,
  proseFormatCommand,
  removeTab,
} from "./commands";

/** Commands that need shell UI (URL dialog, file picker) before they can run. */
export type ProseShellRequest = "link" | "image" | "find";

const item = zwdocSchema.nodes.list_item;

/** Caret to the start / end of the document (Mod-Home / Mod-End, as before). */
function docEdge(atEnd: boolean): Command {
  return (state, dispatch) => {
    const selection = atEnd
      ? Selection.atEnd(state.doc)
      : Selection.atStart(state.doc);
    dispatch?.(state.tr.setSelection(selection).scrollIntoView());
    return true;
  };
}

function format(command: FormatCommand): Command {
  return proseFormatCommand(command) ?? (() => false);
}

/**
 * Editor shortcuts (Mod = Cmd on macOS).
 * Link / image hand off to the shell (`onRequest`); Mod-Shift-M adds a comment.
 */
export function proseKeymaps(
  onRequest: (command: ProseShellRequest) => void,
  addComment: () => void,
): Plugin[] {
  const request =
    (command: ProseShellRequest): Command =>
    () => {
      onRequest(command);
      return true;
    };
  const bindings: Record<string, Command> = {
    "Mod-k": request("link"),
    "Mod-Shift-k": request("image"),
    "Mod-f": request("find"),
    // Always consume, so the browser never acts on the chord.
    "Mod-Shift-m": () => {
      addComment();
      return true;
    },
    "Mod-g": findNext,
    "Shift-Mod-g": findPrev,
    "Mod-Home": docEdge(false),
    "Mod-End": docEdge(true),
    "Mod-z": undo,
    "Shift-Mod-z": redo,
    "Mod-y": redo,
    "Mod-b": format("bold"),
    "Mod-i": format("italic"),
    "Mod-u": format("underline"),
    "Mod-Shift-x": format("strikethrough"),
    "Mod-Shift-h": format("highlight"),
    "Mod-Shift-8": format("bulletList"),
    "Mod-Shift-7": format("numberedList"),
    "Mod-Shift-.": format("quote"),
    "Mod-]": format("indent"),
    "Mod-[": format("outdent"),
    Enter: splitListItem(item),
    "Shift-Enter": insertLineBreak,
    Tab: chainCommands(sinkListItem(item), insertTab),
    "Shift-Tab": chainCommands(liftListItem(item), removeTab),
  };
  for (const level of [1, 2, 3, 4, 5, 6] as const) {
    bindings[`Mod-Alt-${level}`] = format(`heading${level}`);
  }
  return [keymap(bindings), keymap(baseKeymap)];
}
